import React from "react";
import { Link } from "react-router-dom";
import {
  ShieldCheck, ClipboardCheck, Camera, FileCheck2, Users, Wrench,
  ArrowRight, Check, ListChecks, FileText, DoorClosed, Building2, Clock,
} from "lucide-react";
import { Button } from "@/components/ui/button";

const BENEFITS = [
  { icon: Camera, title: "Photo-evidenced BS 8214 inspections", body: "Every door is checked against the full standard, with dated photographs of each defect — so your Golden Thread builds itself, not from memory." },
  { icon: ListChecks, title: "Risk-ranked remedial schedules", body: "See exactly what needs doing, how urgent it is and realistic timescales — including manufacture-to-order lead times — so you prioritise budget with confidence." },
  { icon: FileText, title: "Transparent, itemised quotes", body: "Remedial works priced per door with labour and materials shown clearly. A shared Price Log keeps every job consistent — no vague ‘sum for repairs’." },
  { icon: Users, title: "A live client portal", body: "Clients log in to view reports, approve quotes with a signature and track outstanding works — no chasing, no email threads." },
  { icon: FileCheck2, title: "Audit-ready reports", body: "Professional PDF reports for every door and building, ready to hand straight to your auditor or insurer as evidence of due diligence." },
  { icon: ShieldCheck, title: "Nothing falls through the cracks", body: "No-access doors are logged and re-attempted, and completed remedials are verified with post-work reports — so your evidence trail stays up to date." },
];

const STEPS = [
  { icon: ClipboardCheck, title: "Inspect", body: "Guided BS 8214 checklist on any device, online or offline in the field." },
  { icon: FileText, title: "Report", body: "Instant inspection PDF per door and per building." },
  { icon: ListChecks, title: "Quote", body: "Turn failures into a priced remedial schedule in one tap." },
  { icon: Users, title: "Approve", body: "Client signs off works in their portal." },
  { icon: Wrench, title: "Maintain", body: "Assign engineers, evidence the fix, close the loop." },
];

const SHOWCASE = [
  { img: "/marketing/shot-inspection.png", eyebrow: "Inspections", title: "Capture every defect against the standard", body: "Fail a component and the app opens the exact BS 8214 reasons to tick — plus a free-text ‘add your own’ that flows straight into your Price Log. Photograph the issue and move on.", points: ["Single & double-door logic", "Reason tickboxes + custom defects", "Photo evidence on every failure"] },
  { img: "/marketing/shot-quote.png", eyebrow: "Quotes", title: "Priced remedial schedules in seconds", body: "Failures become an itemised quote grouped per door, with labour and materials split out. A shared Price Log keeps pricing consistent across your whole team.", points: ["Per-door totals & bulk pricing", "Labour / materials breakdown", "Custom, branded PDF layouts"] },
  { img: "/marketing/shot-portal.png", eyebrow: "Client Portal", title: "Let clients approve works themselves", body: "Give property managers their own secure login to review reports, sign off quotes with a finger-drawn signature and see exactly what’s outstanding.", points: ["Signature quote approvals", "Live status of every door", "Zero back-and-forth email"] },
  { img: "/marketing/shot-reports.png", eyebrow: "Reports & Golden Thread", title: "Evidence that stands up to scrutiny", body: "One tidy, dated record of every inspection, remedial and re-inspection — ready for the Responsible Person, the auditor or the enforcing authority.", points: ["Professional PDF reports", "Full building history", "Audit-ready Golden Thread"] },
];

const TIERS = [
  {
    key: "basic", name: "Basic", tag: "FD30 · inspect & report",
    from: 40,
    bands: [{ doors: 10, price: 40 }, { doors: 50, price: 100 }, { doors: 200, price: 200 }, { doors: 500, price: 350 }],
    intro: "Everything you need to inspect and evidence:",
    features: ["Door register & inspections aligned to BS 8214", "Professional PDF inspection reports", "QR door labels"],
    highlight: false,
  },
  {
    key: "pro", name: "Pro", tag: "FD60 · quotes, plans & portal",
    from: 80,
    bands: [{ doors: 10, price: 80 }, { doors: 50, price: 180 }, { doors: 200, price: 350 }, { doors: 500, price: 600 }],
    intro: "Everything in Basic, plus:",
    features: ["Floor-plan upload & door pinning", "Smart quotes & shared Price Log", "Client portal & quote approvals", "Excel / CSV exports", "External report import (AI)", "Custom inspection questions", "Editable report & quote templates"],
    highlight: true,
  },
  {
    key: "pro_maintenance", name: "Pro Maintenance", tag: "Full remedial workflow",
    from: 120,
    bands: [{ doors: 10, price: 120 }, { doors: 50, price: 260 }, { doors: 200, price: 500 }, { doors: 500, price: 850 }],
    intro: "Everything in Pro, plus:",
    features: ["Maintenance jobs & engineer portal", "Invoicing & payment chasing", "Contractor branding", "GPS device tracking & live map"],
    highlight: false,
  },
];

const FAQS = [
  { q: "Does this help with the Fire Safety (England) Regulations 2022?", a: "Yes. FD Check is built around Regulation 10 duties — regular fire-door checks with dated, photographic evidence you can produce on demand. Every inspection feeds a Golden-Thread record for the Responsible Person." },
  { q: "Do your inspections follow BS 8214?", a: "Every inspection runs a full checklist aligned to the BS 8214 code of practice covering gaps, seals, hinges, closers, glazing, signage and more, with satisfactory / remedial / advisory outcomes and photo evidence on each issue." },
  { q: "What happens after the 30-day free trial?", a: "Your inspections and reports stay in your account while your trial or subscription is active. During the free trial you can inspect up to 5 doors and view your reports on screen; downloading and printing PDF reports unlocks as soon as you subscribe to a plan." },
  { q: "Can my clients see their own reports?", a: "Yes — invite them to the client portal where they can view reports, approve remedial quotes with a signature and track outstanding works." },
  { q: "Does it work on site with no signal?", a: "Yes. Field inspections capture and auto-save offline, then sync when you’re back in range." },
];

export default function Landing() {
  return (
    <div className="min-h-screen bg-white font-['Inter',system-ui,sans-serif] text-slate-900">
      <Nav />
      <Hero />
      <TrustBar />
      <Benefits />
      <HowItWorks />
      <Showcase />
      <Pricing />
      <Faq />
      <FinalCta />
      <Footer />
    </div>
  );
}

function Nav() {
  return (
    <header className="sticky top-0 z-50 border-b border-slate-100 bg-white/85 backdrop-blur">
      <div className="mx-auto flex max-w-6xl items-center justify-between px-5 py-3">
        <Link to="/" className="flex items-center gap-2" data-testid="landing-logo">
          <img src="/fdcheck-logo.jpg" alt="FD Check" className="h-9 w-auto object-contain" />
        </Link>
        <nav className="hidden items-center gap-7 text-sm font-medium text-slate-600 md:flex">
          <a href="#features" className="hover:text-slate-900">Features</a>
          <a href="#how" className="hover:text-slate-900">How it works</a>
          <a href="#pricing" className="hover:text-slate-900">Pricing</a>
          <a href="#faq" className="hover:text-slate-900">FAQ</a>
          <Link to="/partner/apply" className="hover:text-slate-900" data-testid="nav-contractors">For contractors</Link>
        </nav>
        <div className="flex items-center gap-2">
          <Link to="/login" data-testid="nav-signin">
            <Button variant="ghost" className="text-slate-700">Sign in</Button>
          </Link>
          <Link to="/register" data-testid="nav-signup">
            <Button className="bg-red-600 hover:bg-red-700">Start free trial</Button>
          </Link>
        </div>
      </div>
    </header>
  );
}

function Hero() {
  return (
    <section className="relative overflow-hidden bg-slate-950 text-white">
      <div className="pointer-events-none absolute inset-0 opacity-70"
        style={{ background: "radial-gradient(60% 60% at 15% 10%, rgba(239,68,68,0.22) 0%, transparent 60%), radial-gradient(50% 50% at 90% 20%, rgba(148,163,184,0.12) 0%, transparent 55%)" }} />
      <div className="relative mx-auto grid max-w-6xl items-center gap-12 px-5 py-16 sm:py-20 lg:grid-cols-[1.05fr_0.95fr] lg:py-24">
        <div>
          <span className="inline-flex items-center gap-2 rounded-full border border-red-400/30 bg-red-400/10 px-3 py-1 text-xs font-semibold uppercase tracking-wide text-red-300">
            <ShieldCheck className="h-3.5 w-3.5" /> Fire door inspection & compliance
          </span>
          <h1 className="mt-5 font-['Sora',sans-serif] text-4xl font-extrabold leading-[1.05] tracking-tight sm:text-5xl lg:text-6xl">
            Record every fire-door inspection — from survey to report.
          </h1>
          <p className="mt-5 max-w-xl text-lg leading-relaxed text-slate-300">
            FD Check runs BS 8214 inspections, builds photo-evidenced reports, prices the remedial works and lets your clients sign them off — one platform for your whole compliance workflow.
          </p>
          <div className="mt-8 flex flex-wrap items-center gap-3">
            <Link to="/register" data-testid="hero-start-trial">
              <Button size="lg" className="h-12 bg-red-600 px-6 text-base hover:bg-red-700">
                Start 30-day free trial <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
            </Link>
            <a href="#how" data-testid="hero-how">
              <Button size="lg" variant="outline" className="h-12 border-white/25 bg-white/5 px-6 text-base text-white hover:bg-white/10">
                See how it works
              </Button>
            </a>
          </div>
          <p className="mt-4 text-sm text-slate-400">No card required · up to 5 doors free for 30 days · BS 8214 · Regulation 10 (England)</p>
        </div>
        <div className="relative">
          <BrowserFrame src="/marketing/shot-dashboard.png" alt="FD Check compliance dashboard" />
          <div className="absolute -bottom-8 -right-2 w-32 sm:w-40 lg:-right-6">
            <PhoneFrame src="/marketing/shot-inspection-phone.png" alt="Fire door inspection on mobile" />
          </div>
        </div>
      </div>
    </section>
  );
}

function TrustBar() {
  const items = [
    { icon: ShieldCheck, label: "BS 8214 inspections" },
    { icon: FileCheck2, label: "Golden-Thread evidence" },
    { icon: DoorClosed, label: "FD30 & FD60 doors" },
    { icon: Building2, label: "Whole-portfolio ready" },
  ];
  return (
    <section className="border-b border-slate-100 bg-slate-50">
      <div className="mx-auto grid max-w-6xl grid-cols-2 gap-4 px-5 py-6 sm:grid-cols-4">
        {items.map((it) => (
          <div key={it.label} className="flex items-center justify-center gap-2 text-sm font-medium text-slate-600">
            <it.icon className="h-4 w-4 text-red-600" /> {it.label}
          </div>
        ))}
      </div>
    </section>
  );
}

function Benefits() {
  return (
    <section id="features" className="mx-auto max-w-6xl px-5 py-20 sm:py-24">
      <SectionHeading
        eyebrow="Why FD Check"
        title="Compliance that proves itself"
        sub="Under the Fire Safety (England) Regulations 2022 you must inspect fire doors regularly — and be able to prove it. FD Check turns that obligation into a defensible, always-current record."
      />
      <div className="mt-14 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
        {BENEFITS.map((b) => (
          <div key={b.title} className="group rounded-2xl border border-slate-200 bg-white p-6 transition-all hover:-translate-y-1 hover:border-red-200 hover:shadow-lg" data-testid={`benefit-${b.title.slice(0, 12)}`}>
            <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-red-50 text-red-600 ring-1 ring-red-100 transition-colors group-hover:bg-red-600 group-hover:text-white">
              <b.icon className="h-5 w-5" />
            </div>
            <h3 className="mt-4 font-['Sora',sans-serif] text-lg font-semibold text-slate-900">{b.title}</h3>
            <p className="mt-2 text-sm leading-relaxed text-slate-600">{b.body}</p>
          </div>
        ))}
      </div>
    </section>
  );
}

function HowItWorks() {
  return (
    <section id="how" className="bg-slate-950 text-white">
      <div className="mx-auto max-w-6xl px-5 py-20 sm:py-24">
        <SectionHeading dark eyebrow="How it works" title="Five steps, one clean audit trail"
          sub="From the first inspection to the signed-off fix, everything is captured in one place." />
        <div className="mt-14 grid gap-5 sm:grid-cols-2 lg:grid-cols-5">
          {STEPS.map((s, i) => (
            <div key={s.title} className="relative rounded-2xl border border-white/10 bg-white/[0.04] p-5">
              <div className="flex items-center gap-3">
                <span className="flex h-9 w-9 items-center justify-center rounded-lg bg-red-500/15 text-red-300">
                  <s.icon className="h-5 w-5" />
                </span>
                <span className="font-['Sora',sans-serif] text-sm font-bold text-red-300">0{i + 1}</span>
              </div>
              <h3 className="mt-4 font-['Sora',sans-serif] text-lg font-semibold">{s.title}</h3>
              <p className="mt-1.5 text-sm leading-relaxed text-slate-400">{s.body}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function Showcase() {
  return (
    <section className="mx-auto max-w-6xl space-y-20 px-5 py-20 sm:py-24">
      {SHOWCASE.map((s, i) => (
        <div key={s.title} className={`grid items-center gap-10 lg:grid-cols-2 ${i % 2 ? "lg:[&>div:first-child]:order-2" : ""}`}>
          <div>
            <span className="text-xs font-bold uppercase tracking-widest text-red-600">{s.eyebrow}</span>
            <h3 className="mt-3 font-['Sora',sans-serif] text-2xl font-bold tracking-tight text-slate-900 sm:text-3xl">{s.title}</h3>
            <p className="mt-3 text-base leading-relaxed text-slate-600">{s.body}</p>
            <ul className="mt-5 space-y-2.5">
              {s.points.map((p) => (
                <li key={p} className="flex items-start gap-2.5 text-sm text-slate-700">
                  <Check className="mt-0.5 h-4 w-4 shrink-0 text-red-600" /> {p}
                </li>
              ))}
            </ul>
          </div>
          <BrowserFrame src={s.img} alt={s.title} />
        </div>
      ))}
    </section>
  );
}

function Pricing() {
  return (
    <section id="pricing" className="bg-slate-50">
      <div className="mx-auto max-w-6xl px-5 py-20 sm:py-24">
        <SectionHeading eyebrow="Pricing"
          title="Start free. Scale when you’re ready."
          sub="Every plan begins with a 30-day free trial — up to 5 doors, no card required. Choose the tier that matches how far you take the workflow." />
        <div className="mt-14 grid gap-6 lg:grid-cols-3">
          {TIERS.map((t) => (
            <div key={t.key}
              className={`relative flex flex-col rounded-2xl border bg-white p-7 ${t.highlight ? "border-red-500 shadow-xl ring-1 ring-red-500/20 lg:-translate-y-3" : "border-slate-200 shadow-sm"}`}
              data-testid={`pricing-${t.key}`}>
              {t.highlight && (
                <span className="absolute -top-3 left-1/2 -translate-x-1/2 rounded-full bg-red-600 px-3 py-1 text-xs font-bold uppercase tracking-wide text-white">Most popular</span>
              )}
              <h3 className="font-['Sora',sans-serif] text-xl font-bold text-slate-900">{t.name}</h3>
              <p className="mt-1 text-sm font-medium text-red-700">{t.tag}</p>
              <div className="mt-5 flex items-baseline gap-1">
                <span className="text-sm font-medium text-slate-500">from</span>
                <span className="font-['Sora',sans-serif] text-3xl font-extrabold text-slate-900">£{t.from}</span>
                <span className="text-sm text-slate-500">/year</span>
              </div>
              <p className="mt-2 text-xs text-slate-400">30-day free trial first · up to 5 doors, no card required</p>
              <ul className="mt-4 space-y-1.5 rounded-xl bg-slate-50 p-3.5" data-testid={`pricing-${t.key}-bands`}>
                {t.bands.map((b) => (
                  <li key={b.doors} className="flex items-center justify-between text-sm">
                    <span className="text-slate-600">Up to {b.doors} doors</span>
                    <span className="font-semibold text-slate-900">£{b.price}<span className="text-xs font-normal text-slate-400">/yr</span></span>
                  </li>
                ))}
              </ul>
              <p className="mt-4 text-sm font-semibold text-slate-800">{t.intro}</p>
              <ul className="mt-3 flex-1 space-y-2.5">
                {t.features.map((f) => (
                  <li key={f} className="flex items-start gap-2.5 text-sm text-slate-600">
                    <Check className="mt-0.5 h-4 w-4 shrink-0 text-red-600" /> {f}
                  </li>
                ))}
              </ul>
              <div className="mt-7">
                <Link to="/register" data-testid={`pricing-${t.key}-cta`}>
                  <Button className={`w-full ${t.highlight ? "bg-red-600 hover:bg-red-700" : ""}`} variant={t.highlight ? "default" : "outline"}>
                    Start free trial
                  </Button>
                </Link>
              </div>
            </div>
          ))}
        </div>
        <p className="mt-8 flex items-center justify-center gap-2 text-sm text-slate-500">
          <Clock className="h-4 w-4 text-red-600" /> On the free trial, reports are view-only; they become downloadable, printable PDFs the moment you subscribe.
        </p>
        <p className="mt-3 text-center text-sm text-slate-600" data-testid="pricing-volume">
          More than 500 doors or multiple sites?{" "}
          <a href="mailto:hello@fdcheck.ltd?subject=FD%20Check%20volume%20licensing%20enquiry"
            className="font-semibold text-red-700 underline-offset-2 hover:underline" data-testid="pricing-volume-contact">
            Contact us for volume licensing
          </a>.
        </p>
      </div>
    </section>
  );
}

function Faq() {
  return (
    <section id="faq" className="mx-auto max-w-3xl px-5 py-20 sm:py-24">
      <SectionHeading eyebrow="FAQ" title="Questions, answered" />
      <div className="mt-10 divide-y divide-slate-200 rounded-2xl border border-slate-200 bg-white">
        {FAQS.map((f, i) => (
          <details key={f.q} className="group px-6 py-5" data-testid={`faq-${i}`}>
            <summary className="flex cursor-pointer list-none items-center justify-between gap-4 font-['Sora',sans-serif] text-base font-semibold text-slate-900">
              {f.q}
              <span className="text-red-600 transition-transform group-open:rotate-45">+</span>
            </summary>
            <p className="mt-3 text-sm leading-relaxed text-slate-600">{f.a}</p>
          </details>
        ))}
      </div>
    </section>
  );
}

function FinalCta() {
  return (
    <section className="bg-red-600">
      <div className="mx-auto flex max-w-6xl flex-col items-center gap-6 px-5 py-16 text-center sm:py-20">
        <h2 className="max-w-2xl font-['Sora',sans-serif] text-3xl font-extrabold tracking-tight text-white sm:text-4xl">
          Ready to make fire-door compliance effortless?
        </h2>
        <p className="max-w-xl text-red-50">Set up in minutes, inspect your first doors today, and hand your clients audit-ready evidence.</p>
        <div className="flex flex-wrap items-center justify-center gap-3">
          <Link to="/register" data-testid="cta-start-trial">
            <Button size="lg" className="h-12 bg-white px-6 text-base text-red-700 hover:bg-red-50">
              Start 30-day free trial <ArrowRight className="ml-2 h-4 w-4" />
            </Button>
          </Link>
          <Link to="/login" data-testid="cta-signin">
            <Button size="lg" variant="outline" className="h-12 border-white/40 bg-transparent px-6 text-base text-white hover:bg-white/10">
              Sign in
            </Button>
          </Link>
        </div>
      </div>
    </section>
  );
}

function Footer() {
  return (
    <footer className="bg-slate-950 text-slate-400">
      <div className="mx-auto flex max-w-6xl flex-col items-center justify-between gap-6 px-5 py-10 sm:flex-row">
        <div className="flex items-center gap-3">
          <img src="/fdcheck-logo.jpg" alt="FD Check" className="h-8 w-auto rounded bg-white/90 p-1 object-contain" />
          <span className="text-sm">Fire door inspection & compliance</span>
        </div>
        <div className="flex items-center gap-6 text-sm">
          <a href="#features" className="hover:text-white">Features</a>
          <a href="#pricing" className="hover:text-white">Pricing</a>
          <Link to="/login" className="hover:text-white" data-testid="footer-signin">Sign in</Link>
          <Link to="/portal/login" className="hover:text-white" data-testid="footer-portal">Client portal</Link>
          <Link to="/partner/apply" className="hover:text-white" data-testid="footer-contractors">Contractor sign-up</Link>
          <Link to="/legal/privacy" className="hover:text-white" data-testid="footer-privacy-link">Privacy</Link>
          <Link to="/legal/terms" className="hover:text-white" data-testid="footer-terms-link">Terms</Link>
        </div>
      </div>
      <div className="border-t border-white/10 py-5 text-center text-xs text-slate-500">
        © {new Date().getFullYear()} FD Check Ltd · All rights reserved
      </div>
    </footer>
  );
}

function SectionHeading({ eyebrow, title, sub, dark }) {
  return (
    <div className="mx-auto max-w-2xl text-center">
      <span className="text-xs font-bold uppercase tracking-widest text-red-600">{eyebrow}</span>
      <h2 className={`mt-3 font-['Sora',sans-serif] text-3xl font-extrabold tracking-tight sm:text-4xl ${dark ? "text-white" : "text-slate-900"}`}>{title}</h2>
      {sub && <p className={`mt-4 text-base leading-relaxed ${dark ? "text-slate-300" : "text-slate-600"}`}>{sub}</p>}
    </div>
  );
}

function BrowserFrame({ src, alt }) {
  return (
    <div className="overflow-hidden rounded-xl border border-slate-200/70 bg-white shadow-2xl ring-1 ring-black/5">
      <div className="flex items-center gap-1.5 border-b border-slate-100 bg-slate-50 px-3 py-2.5">
        <span className="h-2.5 w-2.5 rounded-full bg-red-400" />
        <span className="h-2.5 w-2.5 rounded-full bg-amber-400" />
        <span className="h-2.5 w-2.5 rounded-full bg-emerald-400" />
      </div>
      <img src={src} alt={alt} loading="lazy" className="block w-full" />
    </div>
  );
}

function PhoneFrame({ src, alt }) {
  return (
    <div className="rounded-[1.8rem] border-[6px] border-slate-800 bg-slate-800 shadow-2xl">
      <div className="overflow-hidden rounded-[1.35rem] bg-white">
        <img src={src} alt={alt} loading="lazy" className="block w-full" />
      </div>
    </div>
  );
}
